import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { DollarSign, TrendingUp, TrendingDown, Plus, Trash2, Calendar, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '@/components/PageHeader';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

const TotalRevenue = ({ user }) => {
  const [loading, setLoading] = useState(true);
  
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  
  const [summaryData, setSummaryData] = useState(null);
  const [chartData, setChartData] = useState([]);
  
  // Grafik filtresi
  const [chartFilter, setChartFilter] = useState('thisMonth');
  
  // Form states
  const [newSubscriptionAmount, setNewSubscriptionAmount] = useState('');
  const [newStaffSupportAmount, setNewStaffSupportAmount] = useState('');
  const [newIncomeName, setNewIncomeName] = useState('');
  const [newIncomeAmount, setNewIncomeAmount] = useState('');
  const [newExpenseName, setNewExpenseName] = useState('');
  const [newExpenseAmount, setNewExpenseAmount] = useState('');

  const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  const monthNamesShort = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  const years = [2023, 2024, 2025, 2026];

  useEffect(() => { loadData(); }, [selectedYear, selectedMonth]);
  useEffect(() => { loadChartData(); }, [selectedYear]);

  const getYearMonth = () => `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/revenue-summary?token=${token}&year=${selectedYear}&month=${selectedMonth}`
      );
      if (response.ok) {
        const data = await response.json();
        setSummaryData(data);
      }
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChartData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/revenue-chart-data?token=${token}&year=${selectedYear}`
      );
      if (response.ok) {
        const data = await response.json();
        setChartData(data);
      }
    } catch (error) {
      console.error('Grafik verisi yüklenirken hata:', error);
    }
  };

  // Filtrelenmiş grafik verisi ve widget hesaplamaları
  const filteredChartData = useMemo(() => {
    if (!chartData.length) return [];
    
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    
    switch (chartFilter) {
      case 'thisMonth':
        return chartData.filter(d => d.month === currentMonth);
      case 'lastMonth':
        const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        return chartData.filter(d => d.month === lastMonth);
      case 'last3Months':
        const last3 = [];
        for (let i = 0; i < 3; i++) {
          let m = currentMonth - i;
          if (m <= 0) m += 12;
          last3.push(m);
        }
        return chartData.filter(d => last3.includes(d.month));
      case 'allTime':
      default:
        return chartData;
    }
  }, [chartData, chartFilter]);

  // Widget toplamları
  const chartWidgetData = useMemo(() => {
    const totalIncome = filteredChartData.reduce((sum, d) => sum + (d.total_income || 0), 0);
    const totalExpenses = filteredChartData.reduce((sum, d) => sum + (d.total_expenses || 0), 0);
    const netProfit = totalIncome - totalExpenses;
    return { totalIncome, totalExpenses, netProfit };
  }, [filteredChartData]);

  const getFilterLabel = () => {
    switch (chartFilter) {
      case 'thisMonth': return 'Bu Ay';
      case 'lastMonth': return 'Geçen Ay';
      case 'last3Months': return 'Son 3 Ay';
      case 'allTime': return 'Tüm Zamanlar';
      default: return '';
    }
  };

  const handleAddSubscription = async () => {
    if (!newSubscriptionAmount) { toast.error('Tutar giriniz'); return; }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fixed-incomes?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ income_type: 'subscription', amount: parseFloat(newSubscriptionAmount), date: getYearMonth() })
      });
      if (response.ok) { toast.success('Abonelik geliri eklendi'); setNewSubscriptionAmount(''); loadData(); loadChartData(); }
    } catch (error) { console.error('Ekleme hatası:', error); }
  };

  const handleAddStaffSupport = async () => {
    if (!newStaffSupportAmount) { toast.error('Tutar giriniz'); return; }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fixed-incomes?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ income_type: 'staff_support', amount: parseFloat(newStaffSupportAmount), date: getYearMonth() })
      });
      if (response.ok) { toast.success('Personel destek geliri eklendi'); setNewStaffSupportAmount(''); loadData(); loadChartData(); }
    } catch (error) { console.error('Ekleme hatası:', error); }
  };

  const handleDeleteFixedIncome = async (incomeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fixed-incomes/${incomeId}?token=${token}`, { method: 'DELETE' });
      if (response.ok) { toast.success('Gelir silindi'); loadData(); loadChartData(); }
    } catch (error) { console.error('Silme hatası:', error); }
  };

  const handleAddIncome = async () => {
    if (!newIncomeName.trim() || !newIncomeAmount) { toast.error('Ad ve tutar giriniz'); return; }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manual-incomes?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: getYearMonth(), amount: parseFloat(newIncomeAmount), name: newIncomeName.trim() })
      });
      if (response.ok) { toast.success('Gelir eklendi'); setNewIncomeName(''); setNewIncomeAmount(''); loadData(); loadChartData(); }
    } catch (error) { console.error('Ekleme hatası:', error); }
  };

  const handleDeleteIncome = async (incomeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manual-incomes/${incomeId}?token=${token}`, { method: 'DELETE' });
      if (response.ok) { toast.success('Gelir silindi'); loadData(); loadChartData(); }
    } catch (error) { console.error('Silme hatası:', error); }
  };

  const handleAddExpense = async () => {
    if (!newExpenseName.trim() || !newExpenseAmount) { toast.error('Ad ve tutar giriniz'); return; }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expenses?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: getYearMonth(), amount: parseFloat(newExpenseAmount), description: newExpenseName.trim() })
      });
      if (response.ok) { toast.success('Gider eklendi'); setNewExpenseName(''); setNewExpenseAmount(''); loadData(); loadChartData(); }
    } catch (error) { console.error('Ekleme hatası:', error); }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expenses/${expenseId}?token=${token}`, { method: 'DELETE' });
      if (response.ok) { toast.success('Gider silindi'); loadData(); loadChartData(); }
    } catch (error) { console.error('Silme hatası:', error); }
  };

  const formatCurrency = (value) => (value || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';
  
  const formatPeriod = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length >= 2) {
      const monthIndex = parseInt(parts[1]) - 1;
      return `${monthNamesShort[monthIndex]} ${parts[0]}`;
    }
    return dateStr;
  };

  if (loading && !summaryData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      <PageHeader icon={DollarSign} title="Toplam Gelir" subtitle="Aylık gelir, gider ve kâr/zarar analizi" />

      {/* Filtreler */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <Calendar className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Dönem:</span>
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium">
            {monthNames.map((name, index) => (<option key={index} value={index + 1}>{name}</option>))}
          </select>
          <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium">
            {years.map(y => (<option key={y} value={y}>{y}</option>))}
          </select>
          <span className="ml-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
            {monthNames[selectedMonth - 1]} {selectedYear}
          </span>
        </div>
      </div>

      {/* İKİ SÜTUNLU LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        
        {/* SOL - GELİRLER */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            GELİRLER
            <span className="text-xs text-gray-400 font-normal ml-2">({monthNames[selectedMonth - 1]} {selectedYear})</span>
          </h2>
          
          {/* Satış Geliri */}
          <div className="flex justify-between items-center py-2 px-3 bg-green-50 rounded-lg border border-green-200 mb-3">
            <div>
              <span className="text-gray-700 font-medium text-sm">Satış Geliri</span>
              <span className="text-xs text-green-600 ml-1">(Otomatik)</span>
            </div>
            <span className="font-bold text-green-700">{formatCurrency(summaryData?.sales_revenue)}</span>
          </div>

          {/* Abonelik Geliri */}
          <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="text-sm font-medium text-blue-700 mb-2">Abonelik Geliri</h3>
            <div className="flex gap-2 mb-2">
              <input type="number" value={newSubscriptionAmount} onChange={(e) => setNewSubscriptionAmount(e.target.value)}
                placeholder="Tutar (₺)" className="flex-1 border rounded px-3 py-2 text-sm" />
              <Button onClick={handleAddSubscription} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white px-3">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {summaryData?.subscription_incomes?.length > 0 ? (
              <div className="space-y-1">
                {summaryData.subscription_incomes.map((income) => (
                  <div key={income.id} className="flex justify-between items-center py-1 px-2 bg-white rounded text-sm">
                    <span className="text-gray-500">{formatPeriod(income.date)}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-blue-600">{formatCurrency(income.amount)}</span>
                      <button onClick={() => handleDeleteFixedIncome(income.id)} className="text-gray-400 hover:text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="text-right text-xs font-medium text-blue-700 pt-1 border-t">
                  Toplam: {formatCurrency(summaryData?.subscription_revenue)}
                </div>
              </div>
            ) : <p className="text-gray-400 text-xs">Kayıt yok</p>}
          </div>

          {/* Personel Destek */}
          <div className="mb-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
            <h3 className="text-sm font-medium text-purple-700 mb-2">Personel Destek Geliri</h3>
            <div className="flex gap-2 mb-2">
              <input type="number" value={newStaffSupportAmount} onChange={(e) => setNewStaffSupportAmount(e.target.value)}
                placeholder="Tutar (₺)" className="flex-1 border rounded px-3 py-2 text-sm" />
              <Button onClick={handleAddStaffSupport} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white px-3">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {summaryData?.staff_support_incomes?.length > 0 ? (
              <div className="space-y-1">
                {summaryData.staff_support_incomes.map((income) => (
                  <div key={income.id} className="flex justify-between items-center py-1 px-2 bg-white rounded text-sm">
                    <span className="text-gray-500">{formatPeriod(income.date)}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-purple-600">{formatCurrency(income.amount)}</span>
                      <button onClick={() => handleDeleteFixedIncome(income.id)} className="text-gray-400 hover:text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="text-right text-xs font-medium text-purple-700 pt-1 border-t">
                  Toplam: {formatCurrency(summaryData?.staff_support_revenue)}
                </div>
              </div>
            ) : <p className="text-gray-400 text-xs">Kayıt yok</p>}
          </div>

          {/* Diğer Gelirler */}
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Diğer Gelirler</h3>
            <div className="flex gap-2 mb-2">
              <input type="text" value={newIncomeName} onChange={(e) => setNewIncomeName(e.target.value)}
                placeholder="Gelir adı" className="flex-1 border rounded px-3 py-2 text-sm" />
              <input type="number" value={newIncomeAmount} onChange={(e) => setNewIncomeAmount(e.target.value)}
                placeholder="Tutar (₺)" className="w-28 border rounded px-3 py-2 text-sm" />
              <Button onClick={handleAddIncome} size="sm" className="bg-green-600 hover:bg-green-700 text-white px-3">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {summaryData?.manual_incomes?.length > 0 ? (
              <div className="space-y-1">
                {summaryData.manual_incomes.map((income) => (
                  <div key={income.id} className="flex justify-between items-center py-1 px-2 bg-white rounded text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">{formatPeriod(income.date)}</span>
                      <span className="text-gray-700">{income.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-green-600">{formatCurrency(income.amount)}</span>
                      <button onClick={() => handleDeleteIncome(income.id)} className="text-gray-400 hover:text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-gray-400 text-xs">Kayıt yok</p>}
          </div>

          {/* Toplam Gelir */}
          <div className="mt-4 bg-green-600 rounded-lg p-4 text-white">
            <h3 className="text-sm font-medium text-green-100">Toplam Gelir</h3>
            <p className="text-2xl font-bold">{formatCurrency(summaryData?.total_income)}</p>
          </div>
        </div>

        {/* SAĞ - GİDERLER */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-600" />
            GİDERLER
            <span className="text-xs text-gray-400 font-normal ml-2">({monthNames[selectedMonth - 1]} {selectedYear})</span>
          </h2>
          
          {/* Gider Ekleme */}
          <div className="flex gap-2 mb-3">
            <input type="text" value={newExpenseName} onChange={(e) => setNewExpenseName(e.target.value)}
              placeholder="Gider adı" className="flex-1 border rounded px-3 py-2 text-sm" />
            <input type="number" value={newExpenseAmount} onChange={(e) => setNewExpenseAmount(e.target.value)}
              placeholder="Tutar (₺)" className="w-28 border rounded px-3 py-2 text-sm" />
            <Button onClick={handleAddExpense} size="sm" className="bg-red-600 hover:bg-red-700 text-white px-3">
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {/* Gider Listesi */}
          <div className="space-y-2 min-h-[280px]">
            {summaryData?.expenses?.length > 0 ? (
              summaryData.expenses.map((expense) => (
                <div key={expense.id} className="flex justify-between items-center py-2 px-3 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm">{formatPeriod(expense.date)}</span>
                    <span className="text-gray-700 text-sm">{expense.description}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-red-600">-{formatCurrency(expense.amount)}</span>
                    <button onClick={() => handleDeleteExpense(expense.id)} className="text-gray-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-400 text-sm text-center py-8">Gider kaydı yok</p>
            )}
          </div>

          {/* Toplam Gider */}
          <div className="mt-4 bg-red-600 rounded-lg p-4 text-white">
            <h3 className="text-sm font-medium text-red-100">Toplam Gider</h3>
            <p className="text-2xl font-bold">-{formatCurrency(summaryData?.total_expenses)}</p>
          </div>
        </div>
      </div>

      {/* SEÇİLİ DÖNEM NET KÂR/ZARAR */}
      <div className={`rounded-xl shadow-lg p-5 text-white ${(summaryData?.net_profit || 0) >= 0 ? 'bg-blue-600' : 'bg-orange-600'}`}>
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-sm font-medium opacity-80">{monthNames[selectedMonth - 1]} {selectedYear} - Net Kâr / Zarar</h3>
            <p className="text-3xl font-bold">
              {(summaryData?.net_profit || 0) >= 0 ? '+' : ''}{formatCurrency(summaryData?.net_profit)}
            </p>
          </div>
          <div className="text-right text-sm opacity-80">
            <p>Gelir: {formatCurrency(summaryData?.total_income)}</p>
            <p>Gider: -{formatCurrency(summaryData?.total_expenses)}</p>
          </div>
        </div>
      </div>

      {/* GRAFİK BÖLÜMÜ - AYRI FİLTRE VE WİDGETLAR */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Kâr-Zarar Grafiği
          </h2>
          
          {/* Grafik Filtreleri */}
          <div className="flex gap-2">
            <Button 
              variant={chartFilter === 'thisMonth' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setChartFilter('thisMonth')}
              className={chartFilter === 'thisMonth' ? 'bg-blue-600' : ''}
            >
              Bu Ay
            </Button>
            <Button 
              variant={chartFilter === 'lastMonth' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setChartFilter('lastMonth')}
              className={chartFilter === 'lastMonth' ? 'bg-blue-600' : ''}
            >
              Geçen Ay
            </Button>
            <Button 
              variant={chartFilter === 'last3Months' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setChartFilter('last3Months')}
              className={chartFilter === 'last3Months' ? 'bg-blue-600' : ''}
            >
              Son 3 Ay
            </Button>
            <Button 
              variant={chartFilter === 'allTime' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setChartFilter('allTime')}
              className={chartFilter === 'allTime' ? 'bg-blue-600' : ''}
            >
              Tüm Zamanlar
            </Button>
          </div>
        </div>

        {/* Grafik Widget'ları */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-xs text-green-600 font-medium">{getFilterLabel()} - Toplam Gelir</p>
            <p className="text-xl font-bold text-green-700">{formatCurrency(chartWidgetData.totalIncome)}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-xs text-red-600 font-medium">{getFilterLabel()} - Toplam Gider</p>
            <p className="text-xl font-bold text-red-700">-{formatCurrency(chartWidgetData.totalExpenses)}</p>
          </div>
          <div className={`rounded-lg p-4 ${chartWidgetData.netProfit >= 0 ? 'bg-blue-50 border border-blue-200' : 'bg-orange-50 border border-orange-200'}`}>
            <p className={`text-xs font-medium ${chartWidgetData.netProfit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>{getFilterLabel()} - Net Kâr/Zarar</p>
            <p className={`text-xl font-bold ${chartWidgetData.netProfit >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
              {chartWidgetData.netProfit >= 0 ? '+' : ''}{formatCurrency(chartWidgetData.netProfit)}
            </p>
          </div>
        </div>

        {/* Grafik */}
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={filteredChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#6b7280" />
              <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} />
              <Tooltip formatter={(value) => [formatCurrency(value), '']}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
              <Legend />
              <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="3 3" />
              <Bar dataKey="total_income" name="Toplam Gelir" fill="#22c55e" radius={[4, 4, 0, 0]} />
              <Bar dataKey="total_expenses" name="Toplam Gider" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Bar dataKey="net_profit" name="Net Kâr" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default TotalRevenue;
